import Ws from '@adonisjs/websocket-client'
import { useEffect, useRef, useState } from 'react'

import {
  STATUS_PAYMENT_METHOD_ACCEPTED,
  STATUS_PAYMENT_METHOD_NOT_READY_YET,
  STATUS_PAYMENT_METHOD_REJECTED,
} from '@features/payment/lib/const'

const WS_URL = import.meta.env.VITE_WS_API

type Message = {
  status: number;
  metadata?: Record<string, unknown>;
};

type Subscription = {
  on: (event: string, callback: (message: Message) => void) => void;
  emit: (event: string, data: { operationId: string }) => void;
};

type WebSocketConnection = {
  on: (event: string, callback: () => void) => void;
  subscribe: (channel: string) => Subscription;
  close: () => void;
};

type WebSocketInstance = {
  connect: () => WebSocketConnection;
  close: () => void;
};

export default function usePaymentVerificator ({
  operationId,
}: {
  operationId: string;
}) {
  const [ state, setState ] = useState<{
    verified: boolean;
    rejected: boolean;
    metadata: Record<string, unknown>;
    isLoading: boolean;
  }>({
    verified: false,
    rejected: false,
    metadata: {},
    isLoading: true,
  })

  const websocket = useRef<WebSocketInstance | null>(null)
  const pingTimer = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const ws = Ws(WS_URL) as unknown as WebSocketInstance
    websocket.current = ws

    const conn = websocket.current.connect()

    conn.on('open', () => {
      const subscription = conn.subscribe(`payment-status:${operationId}`)
      subscription.on('statusChanged', (message: Message) => {
        const { status, metadata = {} } = message

        if (status === STATUS_PAYMENT_METHOD_NOT_READY_YET) {
          return
        }

        if (pingTimer.current) {
          clearInterval(pingTimer.current)
        }

        if (status === STATUS_PAYMENT_METHOD_ACCEPTED) {
          setState((prevState) => ({
            ...prevState,
            verified: true,
            metadata,
            isLoading: false,
          }))
        }
        else if (status === STATUS_PAYMENT_METHOD_REJECTED) {
          setState((prevState) => ({
            ...prevState,
            rejected: true,
            metadata,
            isLoading: false,
          }))
        }
      })

      subscription.on('ready', () => {
        pingTimer.current = setInterval(() => {
          subscription.emit('statusCheck', { operationId })
        }, 5000)
      })

      conn.on('close', () => {
        if (pingTimer.current) {
          clearInterval(pingTimer.current)
        }
      })
    })

    return () => {
      if (websocket.current) {
        websocket.current.close()
      }
      if (pingTimer.current) {
        clearInterval(pingTimer.current)
      }
    }
  }, [ operationId ])

  return state
}
